import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

import { OverlayTemplateFolder } from '@/lomavisSharedUi/types/overlayTemplates';

export interface LomavisTemplateMoveFolderModalLabels {
  title: string;
  template: string;
  folder: string;
  noFolder: string;
  cancel: string;
  save: string;
  saving: string;
}

export interface LomavisTemplateMoveFolderModalProps {
  show: boolean;
  templateTitle?: string;
  folderId: string | null;
  initialFolderId: string | null;
  folders: OverlayTemplateFolder[];
  labels: LomavisTemplateMoveFolderModalLabels;
  validationMessage?: string;
  isSubmitting?: boolean;
  onFolderChange: (folderId: string | null) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

/** Organism: assigns an existing template to a folder or removes its folder. */
export const LomavisTemplateMoveFolderModal: React.FC<
  LomavisTemplateMoveFolderModalProps
> = ({
  show,
  templateTitle,
  folderId,
  initialFolderId,
  folders,
  labels,
  validationMessage,
  isSubmitting = false,
  onFolderChange,
  onCancel,
  onSubmit
}) => {
  const cannotSubmit = isSubmitting || folderId === initialFolderId;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!cannotSubmit) onSubmit();
  };

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? () => undefined : onCancel}
      centered
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title className="fs-5 fw-bold">{labels.title}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="d-flex flex-column gap-3">
          {templateTitle && (
            <div>
              <div className="text-muted fs-8 fw-semibold mb-1">
                {labels.template}
              </div>
              <div className="fw-semibold text-dark">{templateTitle}</div>
            </div>
          )}

          <Form.Group controlId="overlay-template-folder-selection">
            <Form.Label>{labels.folder}</Form.Label>
            <Form.Select
              size="sm"
              autoFocus
              value={folderId ?? ''}
              isInvalid={Boolean(validationMessage)}
              aria-invalid={Boolean(validationMessage)}
              disabled={isSubmitting}
              onChange={(event) => onFolderChange(event.target.value || null)}
            >
              <option value="">{labels.noFolder}</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </Form.Select>
            {validationMessage && (
              <Form.Control.Feedback type="invalid">
                {validationMessage}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            size="sm"
            variant="white"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {labels.cancel}
          </Button>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            disabled={cannotSubmit}
          >
            {isSubmitting && (
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
            )}
            {isSubmitting ? labels.saving : labels.save}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
